/*  CourseActivityFeed — Chronological audit-log view for a course.
    ────────────────────────────────────────────────────────────────
    Reusable across:
        • Author-side CourseSetup (creator scope endpoint)
        • Admin-side CourseReviewDetail (admin scope endpoint)

    Props:
        courseId  (string, required)
        endpoint  (string, default `/course-builder/courses/{id}/activity`
                          admin passes `/admin/course-review/courses/{id}/activity`)
        title     (string, default "Activity")
        compact   (bool)   — when true, renders inside a card without the
                             outer heading (used as a right-rail widget).
*/
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ClockCounterClockwise, User as UserIcon, ArrowClockwise,
    PencilSimple, Trash, Plus, CheckCircle, ArrowUUpLeft, ListChecks,
    Circle, Books, Article, PaperPlaneTilt, XCircle, Question as QIcon,
    NotePencil,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";


// Map action codes to a friendly label + icon + tone.
const ACTION_META = {
    course_create:      { label: "Course created",       Icon: Plus,          tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    course_update:      { label: "Course edited",        Icon: PencilSimple,  tone: "text-sky-700 bg-sky-50 border-sky-200" },
    course_submit_review:{label: "Submitted for review", Icon: PaperPlaneTilt,tone: "text-amber-700 bg-amber-50 border-amber-200" },
    course_approve:     { label: "Approved & published", Icon: CheckCircle,   tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    course_reject:      { label: "Rejected",             Icon: XCircle,       tone: "text-rose-700 bg-rose-50 border-rose-200" },
    course_unpublish:   { label: "Unpublished",          Icon: ArrowUUpLeft,  tone: "text-amber-700 bg-amber-50 border-amber-200" },
    course_delete:      { label: "Deleted",              Icon: Trash,         tone: "text-rose-700 bg-rose-50 border-rose-200" },
    module_add:         { label: "Module added",         Icon: Books,         tone: "text-violet-700 bg-violet-50 border-violet-200" },
    module_delete:      { label: "Module removed",       Icon: Books,         tone: "text-rose-700 bg-rose-50 border-rose-200" },
    lesson_add:         { label: "Lesson added",         Icon: Article,       tone: "text-teal-700 bg-teal-50 border-teal-200" },
    lesson_delete:      { label: "Lesson removed",       Icon: Article,       tone: "text-rose-700 bg-rose-50 border-rose-200" },
    final_exam_update:  { label: "Final exam edited",    Icon: QIcon,         tone: "text-rose-700 bg-rose-50 border-rose-200" },
};

const DEFAULT_META = { label: (a) => a.replace(/_/g, " "), Icon: NotePencil, tone: "text-slate-700 bg-slate-50 border-slate-200" };


function timeAgo(iso) {
    if (!iso) return "—";
    const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    if (secs < 30 * 86400) return `${Math.round(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}


function fmtTimestamp(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}


export default function CourseActivityFeed({
    courseId,
    endpoint,
    title = "Activity",
    compact = false,
    testid = "course-activity-feed",
}) {
    const url = endpoint || `/course-builder/courses/${courseId}/activity`;
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (isRefresh = false) => {
        if (!courseId) { setLoading(false); return; }
        if (isRefresh) setRefreshing(true); else setLoading(true);
        try {
            const { data } = await api.get(url);
            setEntries(data.entries || []);
        } catch (e) {
            if (!isRefresh) toast.error(e?.response?.data?.detail || "Couldn't load activity.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [courseId, url]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className={compact ? "" : "bg-card border border-border rounded-2xl p-4"} data-testid={testid}>
            {!compact && (
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <ClockCounterClockwise size={16} weight="duotone" className="text-primary" />
                        <div className="text-sm font-bold">{title}</div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                            {entries.length}
                        </span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={refreshing} data-testid={`${testid}-refresh`}>
                        <ArrowClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
                    </Button>
                </div>
            )}

            {loading ? (
                <div className="py-6 text-center text-xs text-muted-foreground" data-testid={`${testid}-loading`}>Loading activity…</div>
            ) : entries.length === 0 ? (
                <div className="py-6 text-center border border-dashed border-border rounded-xl" data-testid={`${testid}-empty`}>
                    <Circle size={22} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
                    <div className="text-xs font-semibold">No activity yet</div>
                    <p className="text-[11px] text-muted-foreground mt-1">Every edit, publish, and deletion will appear here.</p>
                </div>
            ) : (
                <div className="space-y-2" data-testid={`${testid}-list`}>
                    {entries.map((e, i) => {
                        const meta = ACTION_META[e.action] || DEFAULT_META;
                        const Icon = meta.Icon;
                        const label = typeof meta.label === "function" ? meta.label(e.action) : meta.label;
                        const reason = e.meta?.reason;
                        return (
                            <div
                                key={e.id || i}
                                className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border bg-secondary/20 hover:bg-secondary/40 transition-colors"
                                data-testid={`${testid}-entry-${i}`}
                            >
                                <div className={`w-7 h-7 shrink-0 rounded-md grid place-items-center border ${meta.tone}`}>
                                    <Icon size={12} weight="fill" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[12px] font-bold" data-testid={`${testid}-entry-${i}-label`}>{label}</span>
                                        {e.summary && (
                                            <span className="text-[11px] text-muted-foreground truncate">· {e.summary}</span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-muted-foreground flex-wrap">
                                        <span className="inline-flex items-center gap-1">
                                            <UserIcon size={9} weight="duotone" />
                                            <span className="font-semibold" data-testid={`${testid}-entry-${i}-actor`}>
                                                {e.actor_name || "System"}
                                            </span>
                                            {e.actor_role && (
                                                <span className="uppercase text-[9px] tracking-wider">· {e.actor_role.replace("_", " ")}</span>
                                            )}
                                        </span>
                                        <span title={e.created_at} data-testid={`${testid}-entry-${i}-time`}>
                                            {fmtTimestamp(e.created_at)} · {timeAgo(e.created_at)}
                                        </span>
                                    </div>
                                    {reason && (
                                        <div className="mt-1.5 text-[11px] text-rose-900 bg-rose-50/60 border border-rose-200 rounded px-2 py-1.5 leading-snug" data-testid={`${testid}-entry-${i}-reason`}>
                                            <span className="font-bold text-[9px] uppercase tracking-wider text-rose-700 mr-1">Reason:</span>
                                            {reason}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
